import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CircleAlert,
  ExternalLink,
  Pause,
  Play,
  Printer,
  RotateCcw,
  Settings2,
} from 'lucide-react';
import clsx from 'clsx';
import {
  PRINT_JOB_KIND_LABELS,
  PRINT_JOB_STATUS_LABELS,
  type ClaimedPrintJob,
  type PrintJob,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { POLL, usePrintJobs } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { formatClock } from '../lib/format';
import { ReceiptSheet } from '../components/ReceiptSheet';
import { Badge, Button, Card, EmptyState, Field, Input, Spinner } from '../components/ui';

const STATION_NAME_KEY = 'eltoro.printStation.name';
const RUNNING_KEY = 'eltoro.printStation.running';

function readStored(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Waits until the browser has actually laid out and painted the document.
 *
 * `window.print()` snapshots the page as it stands at that instant, so printing
 * in the same tick as the state update yields a blank sheet. Two frames covers
 * layout; the image wait covers the restaurant logo, which would otherwise be
 * missing from the first bill after a cold start.
 */
async function waitForPaint(node: HTMLElement | null): Promise<void> {
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
  const images = [...(node?.querySelectorAll('img') ?? [])];
  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) return resolve();
          const finish = () => resolve();
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
          // Never let one broken logo hold the whole queue.
          setTimeout(finish, 2_000);
        }),
    ),
  );
}

/**
 * The print station: the screen the cashier's PC leaves open so that bills
 * queued from a phone come out of the printer wired to that PC.
 *
 * It claims jobs, renders each one into an off-screen stage, calls
 * `window.print()`, and reports the outcome. With Chrome started under
 * `--kiosk-printing` there is no dialog at all — paper simply appears.
 */
export function PrintStationPage() {
  const { home } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [stationName, setStationName] = useState(() => readStored(STATION_NAME_KEY, 'Cashier'));
  // Held separately from `stationName`: committing per keystroke would tear the
  // claim loop down and rebuild it on every letter typed.
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [running, setRunning] = useState(() => readStored(RUNNING_KEY, 'true') === 'true');
  const [printing, setPrinting] = useState<ClaimedPrintJob | null>(null);
  const [testing, setTesting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  // Guards the claim loop: one print run at a time, no matter how the timer fires.
  const busyRef = useRef(false);
  const audioRef = useRef<AudioContext | null>(null);

  const { data: jobs, refetch } = usePrintJobs({ limit: 40 });
  const queued = (jobs ?? []).filter((job) => job.status === 'QUEUED' || job.status === 'PRINTING');

  useEffect(() => {
    try {
      localStorage.setItem(STATION_NAME_KEY, stationName);
      localStorage.setItem(RUNNING_KEY, String(running));
    } catch {
      // Private browsing: the station still works, it just forgets its name.
    }
  }, [stationName, running]);

  /** A short chime, so a cashier facing away knows a bill is coming out. */
  const chime = useCallback(() => {
    const context = audioRef.current;
    if (!context) return;
    try {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.25);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.26);
    } catch {
      // Sound is a courtesy; never let it break the print loop.
    }
  }, []);

  const commitName = () => {
    const next = (nameDraft ?? '').trim();
    if (next.length > 0) setStationName(next);
    setNameDraft(null);
  };

  /** Browsers only allow audio after a gesture, so the Start button opens it. */
  const start = () => {
    if (!audioRef.current) {
      try {
        audioRef.current = new AudioContext();
      } catch {
        audioRef.current = null;
      }
    }
    void audioRef.current?.resume();
    setRunning(true);
  };

  const printOne = useCallback(
    async (job: ClaimedPrintJob) => {
      setPrinting(job);
      try {
        await waitForPaint(stageRef.current);
        for (let copy = 0; copy < job.copies; copy += 1) {
          window.print();
          // Give the driver a beat between copies rather than racing it.
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
        await api.printJobs.done(job.id);
      } catch (error) {
        const message =
          error instanceof ApiError ? error.message : 'This station could not print the document';
        await api.printJobs.failed(job.id, message).catch(() => undefined);
        setLastError(message);
      } finally {
        setPrinting(null);
      }
    },
    [],
  );

  /* The claim loop. Runs only while the station is live. */
  useEffect(() => {
    if (!running) return;
    let cancelled = false;

    const tick = async () => {
      if (busyRef.current || cancelled) return;
      busyRef.current = true;
      try {
        const claimed = await api.printJobs.claim(stationName);
        setLastPollAt(new Date());
        setLastError(null);
        if (claimed.length > 0) chime();
        for (const job of claimed) {
          if (cancelled) break;
          await printOne(job);
        }
        if (claimed.length > 0) void refetch();
      } catch (error) {
        setLastError(
          error instanceof ApiError ? error.message : 'Lost contact with the server',
        );
      } finally {
        busyRef.current = false;
      }
    };

    const timer = setInterval(() => void tick(), POLL.printStation);
    void tick();

    // Chrome throttles timers on a hidden page to roughly one a minute, so a
    // minimised or fully covered station falls behind. Catch up the instant it
    // is on screen again rather than waiting out the throttled interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [running, stationName, chime, printOne, refetch]);

  const testPrint = async () => {
    setTesting(true);
    try {
      await waitForPaint(stageRef.current);
      window.print();
    } finally {
      setTesting(false);
    }
  };

  const retry = async (job: PrintJob) => {
    try {
      await api.printJobs.retry(job.id);
      void queryClient.invalidateQueries({ queryKey: ['print-jobs'] });
      toast.info('Back on the queue.');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not requeue that job.');
    }
  };

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="no-print flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-b border-line bg-surface px-4 py-3">
        {/*
          A new window, not a navigation. This screen only claims jobs while it
          is mounted, so walking it to the POS would quietly stop the printer.
          The new window shares this Chrome profile, so it inherits both the
          sign-in and --kiosk-printing.
        */}
        <Button
          variant="ghost"
          size="sm"
          icon={<ExternalLink size={16} />}
          onClick={() => window.open(home, '_blank', 'noopener')}
        >
          Open POS
        </Button>

        <div className="flex items-baseline gap-3">
          <span className="font-display text-2xl leading-none text-ink">Print station</span>
          <span className="text-sm text-ink-faint">{stationName}</span>
        </div>

        <span
          className={clsx(
            'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest',
            running
              ? 'bg-positive/15 text-positive'
              : 'bg-ink-faint/10 text-ink-faint',
          )}
        >
          <span
            className={clsx(
              'h-2 w-2 rounded-full',
              running ? 'animate-pulse bg-positive' : 'bg-ink-faint',
            )}
          />
          {running ? 'Live' : 'Paused'}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<Settings2 size={16} />}
            onClick={() => setNameDraft((draft) => (draft === null ? stationName : null))}
          >
            Rename
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Printer size={16} />}
            loading={testing}
            onClick={() => void testPrint()}
          >
            Test print
          </Button>
          {running ? (
            <Button variant="danger" icon={<Pause size={16} />} onClick={() => setRunning(false)}>
              Pause
            </Button>
          ) : (
            <Button variant="primary" icon={<Play size={16} />} onClick={start}>
              Start printing
            </Button>
          )}
        </div>
      </header>

      <div className="no-print min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <div className="mx-auto max-w-[900px] space-y-5">
          {nameDraft !== null ? (
            <Card>
              <Field
                label="Station name"
                hint="Shown in the queue so a site with two tills can tell them apart."
              >
                <Input
                  autoFocus
                  value={nameDraft}
                  maxLength={60}
                  onChange={(event) => setNameDraft(event.target.value)}
                  onBlur={commitName}
                  onKeyDown={(event) => event.key === 'Enter' && commitName()}
                />
              </Field>
            </Card>
          ) : null}

          {!running ? (
            <Card className="border-caution/40 bg-caution/5">
              <div className="flex items-start gap-3">
                <CircleAlert size={20} className="mt-0.5 shrink-0 text-caution" />
                <div className="text-sm text-ink-soft">
                  <div className="font-semibold text-ink">This station is paused.</div>
                  Bills sent from the floor will sit in the queue until you press
                  <span className="text-ink"> Start printing</span>. Leave this window open and
                  visible during service — a background tab is throttled by the browser and will
                  print late.
                </div>
              </div>
            </Card>
          ) : null}

          {lastError ? (
            <Card className="border-danger/40 bg-danger/5">
              <div className="flex items-start gap-3 text-sm">
                <CircleAlert size={20} className="mt-0.5 shrink-0 text-danger" />
                <div>
                  <div className="font-semibold text-danger">{lastError}</div>
                  <div className="text-ink-faint">
                    The station keeps retrying. Anything it could not print stays in the queue.
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <Card>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
                Waiting
              </div>
              <div className="tnum font-display text-3xl text-accent-strong">{queued.length}</div>
            </Card>
            <Card>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
                Now printing
              </div>
              <div className="font-display text-xl text-ink">
                {printing ? `Table ${String(printing.tableNumber).padStart(2, '0')}` : '—'}
              </div>
            </Card>
            <Card>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-faint">
                Last checked
              </div>
              <div className="tnum font-display text-xl text-ink">
                {lastPollAt ? formatClock(lastPollAt.toISOString()) : '—'}
              </div>
            </Card>
          </div>

          {running ? (
            <p className="text-xs text-ink-faint">
              Keep this window on screen during service. Minimised or fully covered, the browser
              slows it to about one check a minute — nothing is lost, but bills print late.
            </p>
          ) : null}

          <div>
            <h2 className="mb-2 font-display text-lg tracking-wide text-ink">Queue</h2>
            {jobs === undefined ? <Spinner label="Loading the queue…" /> : null}
            {jobs?.length === 0 ? (
              <EmptyState
                icon={<Printer size={32} />}
                title="Nothing to print"
                message="Bills sent from the floor will appear here."
              />
            ) : null}
            <div className="space-y-2">
              {(jobs ?? []).map((job) => (
                <Card key={job.id} className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <div className="font-display text-xl leading-none text-ink">
                    Table {String(job.tableNumber).padStart(2, '0')}
                  </div>
                  <div className="text-sm text-ink-faint">
                    {PRINT_JOB_KIND_LABELS[job.kind]} · #{job.receiptNumber ?? job.orderNumber}
                    {job.copies > 1 ? ` · ${job.copies} copies` : ''}
                  </div>
                  <div className="text-xs text-ink-faint">
                    {job.requestedByName ?? 'Unknown'} · {formatClock(job.createdAt)}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {job.errorMessage && job.status !== 'DONE' ? (
                      <span className="max-w-[18rem] truncate text-xs text-danger">
                        {job.errorMessage}
                      </span>
                    ) : null}
                    <Badge
                      tone={
                        job.status === 'DONE'
                          ? 'positive'
                          : job.status === 'FAILED'
                            ? 'danger'
                            : job.status === 'CANCELLED'
                              ? 'neutral'
                              : 'caution'
                      }
                    >
                      {PRINT_JOB_STATUS_LABELS[job.status]}
                    </Badge>
                    {job.status === 'FAILED' || job.status === 'DONE' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<RotateCcw size={14} />}
                        onClick={() => void retry(job)}
                      >
                        {job.status === 'DONE' ? 'Reprint' : 'Retry'}
                      </Button>
                    ) : null}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/*
        The stage. Parked off-screen rather than hidden: `display: none` cannot be
        printed, and the print stylesheet unpins it (see index.css) so it lands on
        the paper on its own.
      */}
      <div ref={stageRef} className="print-stage" aria-hidden>
        {printing ? <ReceiptSheet receipt={printing.receipt} kind={printing.kind} /> : null}
        {testing && !printing ? <TestSheet stationName={stationName} /> : null}
      </div>
    </div>
  );
}

/** A minimal 80mm document, so the printer can be proved before service. */
function TestSheet({ stationName }: { stationName: string }) {
  return (
    <div className="print-sheet mx-auto w-full max-w-[420px] bg-[#faf7f0] px-8 py-8 font-mono text-[13px] text-[#1a1713]">
      <div className="text-center text-xl font-bold uppercase tracking-[0.3em]">Test print</div>
      <div className="my-4 border-t-2 border-dashed border-[#c3b79d]" />
      <div>Station: {stationName}</div>
      <div>Time: {new Date().toLocaleString()}</div>
      <div className="my-4 border-t border-dashed border-[#c3b79d]" />
      <p className="text-[12px] leading-relaxed">
        If you are holding this on an 80mm till roll, the print station is set up correctly and
        bills sent from the floor will come out here.
      </p>
    </div>
  );
}
